import apiClient from '@/lib/api-client';
import { 
  ReaderVersion, 
  ReaderVersionSearchParams, 
  PlatformStats, 
  UpdateCheckResult,
  ApiResponse,
  PaginatedResponse,
  InstallerUploadResult
} from '../types';

const readerVersionService = {
  // Get all versions with pagination
  getVersions: async (params?: ReaderVersionSearchParams & { page?: number; per_page?: number }): Promise<PaginatedResponse<ReaderVersion>> => {
    const response = await apiClient.get('/reader-version', { params });
    return response.data;
  },

  // Search versions
  searchVersions: async (params: ReaderVersionSearchParams): Promise<ApiResponse<ReaderVersion[]>> => {
    const response = await apiClient.post('/reader-version/search', params);
    return response.data;
  },

  // Get version by ID
  getVersion: async (id: string): Promise<ApiResponse<ReaderVersion>> => {
    const response = await apiClient.get(`/reader-version/${id}`);
    return response.data;
  },

  // Create new version
  createVersion: async (data: Partial<ReaderVersion>): Promise<ApiResponse<ReaderVersion>> => {
    const response = await apiClient.post('/reader-version', data);
    return response.data;
  },

  // Upload file cài đặt, trả về link để điền vào download_url
  uploadInstaller: async (
    params: { platform: string; channel: string; file: File; descriptor: File },
    options: { onProgress?: (percent: number) => void; signal?: AbortSignal } = {}
  ): Promise<ApiResponse<InstallerUploadResult>> => {
    const formData = new FormData();
    formData.append('platform', params.platform);
    formData.append('channel', params.channel);
    formData.append('file', params.file);
    // Số hiệu nằm trong mô tả, không gửi riêng: gửi riêng là mở lại đúng chỗ
    // người ta gõ lệch với version thật trong artifact
    formData.append('descriptor', params.descriptor);

    const response = await apiClient.post('/reader-version/installer', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
      // File cài có thể vài trăm MB, không để timeout mặc định cắt ngang
      timeout: 0,
      signal: options.signal,
      onUploadProgress: (event) => {
        if (options.onProgress && event.total) {
          options.onProgress(Math.round((event.loaded / event.total) * 100));
        }
      }
    });
    return response.data;
  },

  // Update version
  updateVersion: async (id: string, data: Partial<ReaderVersion>): Promise<ApiResponse<ReaderVersion>> => {
    const response = await apiClient.put(`/reader-version/${id}`, data);
    return response.data;
  },

  // Delete version
  deleteVersion: async (id: string): Promise<ApiResponse<void>> => {
    const response = await apiClient.delete(`/reader-version/${id}`);
    return response.data;
  },

  // Get versions by platform
  getVersionsByPlatform: async (platform: string): Promise<ApiResponse<ReaderVersion[]>> => {
    const response = await apiClient.get(`/reader-version/platform/${platform}`);
    return response.data;
  },

  // Get latest version for platform
  getLatestVersion: async (platform?: string): Promise<ApiResponse<ReaderVersion | Record<string, ReaderVersion>>> => {
    const url = platform ? `/reader-version/latest/${platform}` : '/reader-version/latest';
    const response = await apiClient.get(url);
    return response.data;
  },

  // Check for updates (public endpoint)
  checkUpdates: async (platform: string, currentVersion: string): Promise<ApiResponse<UpdateCheckResult>> => {
    const response = await apiClient.get('/reader-version/check-updates', {
      params: { platform, current_version: currentVersion }
    });
    return response.data;
  },

  // Get platform statistics
  getPlatformStats: async (): Promise<ApiResponse<PlatformStats>> => {
    const response = await apiClient.get('/reader-version/stats/platforms');
    return response.data;
  },

  // Get available platforms
  getPlatforms: async (): Promise<ApiResponse<string[]>> => {
    const response = await apiClient.get('/reader-version/platforms');
    return response.data;
  }
};

export default readerVersionService;
